import { useCallback, useMemo, useState } from 'react';

import {
  getBoostExtraCoverValues,
  getBoostSelectedExtraCover,
  isBoostExtraCancellation,
} from '@packages/utils';

import { Button, ButtonProps } from '@elements/button';
import { Dropdown } from '@elements/dropdown';
import { Input } from '@elements/input';
import { Tray } from '@elements/tray/tray.component';
import { getSelectedBoostById, useTripStore } from '@store';

type ExtraCancellationDropdownProps = {
  boostId: string;
};

export const ExtraCancellationDropdown = ({ boostId }: ExtraCancellationDropdownProps) => {
  const boost = useTripStore(getSelectedBoostById(boostId));
  const boostIdValue = boost?.boostId;
  const isAdded = boost?.toUpdate?.isAdded ?? boost?.isAdded;
  const isExtraCancellation = isBoostExtraCancellation(boost);
  const selectedExtraCover = getBoostSelectedExtraCover(boost);
  const extraCoverValues = getBoostExtraCoverValues(boost);

  const addBoost = useTripStore(state => state.addBoost);
  const removeBoost = useTripStore(state => state.removeBoost);
  const [isTrayOpen, setIsTrayOpen] = useState(false);

  const value = useMemo(
    () => (isAdded ? (selectedExtraCover ?? 0) : 0),
    [isAdded, selectedExtraCover],
  );

  const addExtraCancellation = useCallback(
    async (newValue: number) => {
      if (!boostIdValue) return;
      await addBoost({
        boostId: boostIdValue,
        updatePayload: {
          boostProperty: {
            selectedExtraCover: newValue,
          },
        },
      });
    },
    [boostIdValue, addBoost],
  );

  const removeExtraCancellation = useCallback(async () => {
    if (!boostIdValue) return;
    return removeBoost({ boostId: boostIdValue });
  }, [boostIdValue, removeBoost]);

  const handleOnClick = useCallback(
    async (newValue: number) => {
      if (!boostIdValue || (isAdded && selectedExtraCover === newValue)) return;
      return newValue === 0 ? removeExtraCancellation() : addExtraCancellation(newValue);
    },
    [boostIdValue, isAdded, selectedExtraCover, removeExtraCancellation, addExtraCancellation],
  );

  const trayOptions = useMemo(
    () =>
      extraCoverValues?.map(item => ({
        title: item.description,
        onClick: () => handleOnClick(item.value),
        value: item.value?.toString() ?? '',
        isSelected: item.value === value,
      })) ?? [],
    [extraCoverValues, handleOnClick, value],
  );

  if (!isExtraCancellation || !extraCoverValues) return null;

  const initialValue =
    extraCoverValues?.find(item => item.value === value) ?? extraCoverValues?.[0];

  return (
    <div className="max-w-40">
      <Dropdown
        className="hidden sm:block"
        items={extraCoverValues.map(item => ({
          label: item.description,
          value: item.value,
          id: item.description,
        }))}
        initialValue={{
          id: initialValue?.description,
          label: initialValue?.description,
          value: initialValue?.value,
        }}
        inputProps={{
          placeholder: '',
          rightIcon: (props: ButtonProps) => <Dropdown.Button {...props} />,
        }}
        onSelect={val => handleOnClick(val as number)}
      />
      <div className="sm:hidden">
        <Input
          value={initialValue.description}
          readOnly
          onClick={() => setIsTrayOpen(true)}
          rightIcon={(props: ButtonProps) => (
            <Button icon="arrow_drop_down" onClick={() => setIsTrayOpen(true)} {...props} />
          )}
        />
        <Tray options={trayOptions} isTrayOpen={isTrayOpen} onClose={() => setIsTrayOpen(false)} />
      </div>
    </div>
  );
};
